import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AiRequestUtilsService } from 'src/app/api/services/ai-request-utils.service';
import { ApiService } from 'src/app/api/services/api.service';
import { environment } from 'src/environments/environment';

export interface GenerateImageRequest {
  prompt: string;
  width: number;
  height: number;
  sessionId: string;
}

export interface GeneratedImageData {
  url: string;
  imageId?: string;
  model?: string;
  aspectRatio?: string;
}

export type GenerateImageResponse =
  | { type: 'editor_ai_image'; data: GeneratedImageData; creditsUsed: number }
  | { type: 'no_credits'; data: string; creditsUsed: 0 }
  | { type: 'content_blocked'; creditsUsed: 0 };

@Injectable({ providedIn: 'root' })
export class ImageAiApiService extends ApiService<GenerateImageResponse> {
  protected override APIUrl =
    environment.AI_SERVICE_URL + this.getResourceUrl();

  constructor(
    protected httpClient: HttpClient,
    private aiRequestUtils: AiRequestUtilsService,
  ) {
    super(httpClient);
  }

  override getResourceUrl(): string {
    return '/api/v1/editor-tools';
  }

  generateImage(input: GenerateImageRequest): Promise<GenerateImageResponse> {
    const { headers, body } = this.aiRequestUtils.buildCall('', {
      sessionId: input.sessionId,
      includeEmail: true,
    });

    body.prompt = input.prompt;
    body.width = input.width;
    body.height = input.height;

    return firstValueFrom(
      super.requestWithHeadersAsObservable(
        '',
        'POST',
        'generate-image',
        headers,
        undefined,
        body,
      ),
    ).catch((error) => {
      // The API signals a content policy violation as an HTTP error whose
      // body is { contentBlocked: true }; surface it as a typed response.
      if (error?.contentBlocked === true) {
        return { type: 'content_blocked', creditsUsed: 0 } as const;
      }
      throw error;
    });
  }
}
